/**
 * GA4 mirror shim.
 *
 * Lightweight, non-invasive helper that mirrors events into Google Analytics 4
 * when `window.gtag` is available. If gtag isn't loaded (no GTM/GA4 snippet on
 * the current page, ad-blocked, SSR, etc.) this is a silent no-op — it never
 * throws, never blocks UI, never loads a parallel GA4 script.
 *
 * Intentional design:
 *  - Does NOT register a duplicate gtag/GTM tag.
 *  - Does NOT require new secrets.
 *  - Mirrors event names verbatim from the Meta side, so dashboards line up.
 *  - Uses the shared HeirWise analytics privacy policy
 *    (`src/lib/analytics/privacy.ts`) — one denylist for GA4 and PostHog.
 */

import { sanitizeAnalyticsProps } from "@/lib/analytics/privacy";
import type { AnyValue } from "@/lib/rowTypes";

export function trackGA4Event(
  eventName: string,
  params: Record<string, AnyValue> = {},
): void {
  try {
    if (typeof window === "undefined") return;
    const gtag = (window as AnyValue).gtag;
    if (typeof gtag !== "function") return; // silent no-op when GA4 not present
    gtag("event", eventName, sanitizeAnalyticsProps(params));
  } catch {
    /* never block UI on analytics */
  }
}
